import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Hit, offer, ProductObject, swatches } from '../../ProductTile-Slider/type';
import Wrapper from 'components/HOC/wrapper';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import Loading from 'components/commercetools-ui/atoms/loading';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, tablet } from 'helpers/utils/screensizes';
import ProductTile from './productTile';
import { P2Badge } from '../../product/product-list-algolia/types';
import PopularProductTile from '../../ProductTile-Slider/Tile';
import useLocale from 'helpers/hooks/useLocale';
type ProductProps = {
  products: Hit[];
  cookies: any;
  p1Badge?: any;
  p2Badge?: P2Badge[];
};

const CategoryCarousel = (algoliaHits: ProductProps) => {
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTablet] = useMediaQuery(tablet);
  const { getCurrency } = useLocale();
  const currency = getCurrency();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolledToStart, setIsScrolledToStart] = useState(true);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

    setIsScrolledToStart(scrollLeft <= 5);
    setIsScrolledToEnd(scrollLeft + clientWidth >= scrollWidth - 5);
  };

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -scrollContainerRef.current.offsetWidth / visibleItems,
        behavior: 'smooth',
      });

      setTimeout(handleScroll, 300);
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: scrollContainerRef.current.offsetWidth / visibleItems,
        behavior: 'smooth',
      });

      setTimeout(handleScroll, 300);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll(); // Initialize scroll state
    }
    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const visibleItems = useMemo(() => {
    if (isDesktopSize) return 4;
    if (isTablet) return 3;
    return 2;
  }, [isDesktopSize, isTablet]);

  const newArray = algoliaHits?.products?.map((product: Hit) => ({
    name: currency === 'GBP' ? product?.name?.['en-GB'] : product?.name?.['en-EU'] || product?.name?.['en-GB'],
    image: (product?.images && product?.images?.[0]) || null,
    price: product?.variants?.[0]?.prices || null,
    categoryKey: product?.categoryKeys,
    slug: currency === 'GBP' ? product?.slug?.['en-GB'] : product?.slug?.['en-EU'] || product?.slug?.['en-GB'],
    standalonePrice: product?.standalonePrice,
    offers: (product?.offer as offer) || [],
    categories: product?.categories || [],
    swatches: product?.swatches || [],
    customFit: product?.customFit || '',
    firstReceiptDate: product?.variants?.[0]?.attributes?.firstReceiptDate || '',
  }));

  const renderItems = (items: ProductObject[]) => {
    const itemWidth = 100 / visibleItems;

    return items?.map((item, index) => (
      <div
        key={index}
        className="flex flex-shrink-0 items-stretch transition-transform duration-300"
        style={{
          width: `${itemWidth}%`,
          minWidth: `${itemWidth}%`,
          flex: `0 0 calc(100% / ${visibleItems})`,
          scrollSnapAlign: 'start',
        }}
      >
        <PopularProductTile product={item} p1BadgeData={algoliaHits?.p1Badge} p2BadgeData={algoliaHits?.p2Badge} />
      </div>
    ));
  };
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 320;
  return (
    <div className="w-full">
      <>
        <Wrapper clearDefaultStyles className="relative px-13 pb-32 pt-16">
          {algoliaHits?.products?.length > 4 && (
            <div className="absolute left-0 right-0 top-1/2 z-20 flex -translate-y-1/2 justify-between px-4">
              <button
                className={`flex h-${isMobileView ? '30' : '40'} w-${
                  isMobileView ? '30' : '40'
                } items-center justify-center rounded-full bg-black shadow-md transition-all duration-300 ${
                  isMobileView ? 'left-2' : 'left-[-30px]'
                }
                 ${isScrolledToStart ? 'pointer-events-none opacity-50' : ''}`}
                onClick={handleScrollLeft}
                disabled={isScrolledToStart}
                aria-disabled={isScrolledToStart}
              >
                <ChevronLeftIcon
                  className={`h-${isMobileView ? '16' : '20'} w-${isMobileView ? '16' : '20'} text-white`}
                />
              </button>
              <button
                className={`flex h-${isMobileView ? '30' : '40'} w-${
                  isMobileView ? '30' : '40'
                } items-center justify-center rounded-full bg-black shadow-md transition-all duration-300 ${
                  isMobileView ? 'right-2' : 'left-[1095px]'
                }
                 ${isScrolledToEnd ? 'pointer-events-none opacity-50' : ''}`}
                onClick={handleScrollRight}
                disabled={isScrolledToEnd}
                aria-disabled={isScrolledToEnd}
              >
                <ChevronRightIcon
                  className={`h-${isMobileView ? '16' : '20'} w-${isMobileView ? '16' : '20'} text-white`}
                />
              </button>
            </div>
          )}
          {algoliaHits?.products?.length === 0 ? (
            <Loading />
          ) : (
            <>
              <div
                ref={scrollContainerRef}
                className={`ml-4 ${
                  algoliaHits?.products?.length === 1 ? 'justify-center sm:justify-start 2xl:ml-[4rem]' : ''
                } flex items-stretch overflow-hidden md:ml-0 ${
                  algoliaHits?.products?.length > 4 ? 'ag__cc__wrapper flex' : 'justify-start 2xl:ml-[4rem]'
                } gap-5`}
                style={{
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch',
                  overflowY: 'hidden',
                  paddingBottom: '10px',
                }}
              >
                {renderItems(newArray as ProductObject[])}
              </div>
            </>
          )}
        </Wrapper>
      </>
    </div>
  );
};

export default CategoryCarousel;
